import { apiFetch } from '../../lib/api';

export interface BagView {
  id: string;
  applicationId: string;
  version: number;
  academicLevelId: string;
  status: string;
  reviewComment?: string | null;
  items: Array<{
    id: string;
    catalogueSubjectId: string;
    academicLevelId: string;
    itemStatus: string;
    subjectName?: string;
    syllabusCode?: string;
    rejectionReason?: string | null;
    rejectionComment?: string | null;
    rejectedAt?: string | null;
    reviewerParty?: string | null;
    stepName?: string | null;
  }>;
}

export interface OfferingSubject {
  catalogueSubjectId: string;
  subjectCode: string;
  subjectName: string;
  cambridgeSyllabusCode: string;
  /** Paise added to the bag by this subject, or null when no price rule resolves. */
  priceMinor: string | null;
}

export interface OfferingSubjectGroup {
  subjectGroupId: string | null;
  subjectGroupCode: string | null;
  subjectGroupName: string;
  sortOrder: number;
  subjects: OfferingSubject[];
}

export interface OfferingSubjectsView {
  groups: OfferingSubjectGroup[];
  selectionRule: { minSubjects: number; maxSubjects: number } | null;
}

export interface FeeEstimateView {
  payableTotalMinor: string;
  payableTotalDisplay: string;
  currency: 'INR';
  groups: Array<{
    label: string;
    quantity: number;
    totalDisplay: string;
    lines: Array<{
      lineKey: string;
      description: string;
      /** Set on per-subject lines, so the UI can label them by subject alone. */
      subjectCode: string | null;
      netAmountMinor: string;
    }>;
  }>;
  notices: Array<{ code: string; text: string; version?: number }>;
}

export interface ApplicationView {
  id: string;
  applicationNumber: string;
  status: string;
  offeringId: string;
  academicBagId: string | null;
  documentsPolicy?: 'MANDATORY' | 'OPTIONAL';
}

export async function fetchApplication(applicationId: string): Promise<ApplicationView> {
  return apiFetch<ApplicationView>(`/applications/${applicationId}`);
}

export async function fetchOfferingSubjects(
  offeringId: string,
  academicLevelId: string,
): Promise<OfferingSubjectsView> {
  return apiFetch<OfferingSubjectsView>(
    `/offerings/${offeringId}/levels/${academicLevelId}/subjects`,
  );
}

export async function fetchBag(bagId: string): Promise<BagView> {
  return apiFetch<BagView>(`/academic-bags/${bagId}`);
}

export async function addBagItem(bagId: string, catalogueSubjectId: string): Promise<void> {
  await apiFetch(`/academic-bags/${bagId}/items`, {
    method: 'POST',
    body: JSON.stringify({ catalogueSubjectId }),
  });
}

export async function removeBagItem(bagId: string, itemId: string): Promise<void> {
  await apiFetch(`/academic-bags/${bagId}/items/${itemId}`, { method: 'DELETE' });
}

export async function requestBagEstimate(bagId: string): Promise<FeeEstimateView> {
  return apiFetch<FeeEstimateView>(`/academic-bags/${bagId}/estimate`, { method: 'POST' });
}

export async function submitSubjectSelection(applicationId: string): Promise<void> {
  await apiFetch(`/approvals/applications/${applicationId}/submit`, { method: 'POST' });
}
